import { AppDispatch } from '../..';
import { AppThunk } from '../../types';
import apis from '../../../utils/apis';
import { handleApiError } from '../utils/utils';
import { showFeedbackModal } from '../UserFeedbackActions';
import _ from 'lodash';

interface MatterDraftData {
  DraftTypeName: string;
  Version: number;
  Content: string;
  Status?: string;
  Justifications?: { [key: string]: string }; 
  Citations?: string[];
}

export const registerMatterDraftListeners = (
  matterId: string,
  draftId: number,
  dispatch: AppDispatch,
  onProgress?: (data: any) => void,
  onComplete?: (data: any) => void,
  onFailure?: (err: string) => void
) => {
  // Socket listeners removed - legacy code
};

export const generateMatterDraftSSE = (
  matterId: string,
  draftId: number,
  userId: string,
  userType: string,
  onProgress?: (progress: any) => void,
  onComplete?: (data: any) => void,
  onFailure?: (err: string) => void
): AppThunk<Promise<void>> => async dispatch => {
  dispatch({ type: "MATTER_DRAFT_JOB_CONNECTING", payload: { matterId, draftId } });
  registerMatterDraftListeners(matterId, draftId, dispatch as AppDispatch, onProgress, onComplete, onFailure);
  // Socket emit removed - legacy code
};

export const getMatterDraft = (
  matterId: string,
  draftId: number,
  version?: number
): AppThunk<Promise<MatterDraftData | null>> => async dispatch => {
  try {
    const response = await apis.get('/matter-draft/get', {
      params: { matter_id: matterId, draft_id: draftId, version },
    });
    if (response.status === 200) {
      const draftData = response.data;
      dispatch({
        type: 'UPDATE_MATTER_DRAFT',
        payload: {
          entityType: 'matter',
          entityId: matterId,
          draftId,
          version: draftData.Version,
          content: draftData.ContentHtml,
          justifications: draftData.Justifications,
          citations: draftData.Metadata?.Citations,
        },
      });
      return draftData;
    } else if (response.status === 404) return null;
    handleApiError(response, dispatch);
    return null;
  } catch (err) {
    handleApiError(err, dispatch);
    return null;
  }
};

export const updateMatterDraft = (
  matterId: string,
  draftId: number,
  version: number,
  content: string,
  status?: string
): AppThunk<Promise<void>> => async dispatch => {
  try {
    const response = await apis.post('/matter-draft/update', {
      matter_id: matterId,
      draft_id: draftId,
      version,
      content,
      status,
    });
    if (response.status === 200) {
      dispatch({
        type: 'UPDATE_MATTER_DRAFT',
        payload: { entityType: 'matter', entityId: matterId, draftId, version, content, status },
      });
      dispatch(showFeedbackModal({ modalType: 'success', duration: 3000, showModal: true, message: 'Draft saved' }));
    } else {
      handleApiError(response, dispatch);
    }
  } catch (err) {
    handleApiError(err, dispatch);
  }
};

export const autoSaveMatterDraft = _.debounce((
  matterId: string,
  draftId: number,
  version: number,
  content: string
) => updateMatterDraft(matterId, draftId, version, content), 5000);

export const markMatterDraftAsFinal = (
  matterId: string,
  draftId: number,
  version: number
): AppThunk<Promise<void>> => async dispatch => {
  try {
    const response = await apis.post('/matter-draft/mark_final', {
      matter_id: matterId,
      draft_id: draftId,
      version,
    });
    if (response.status === 200) {
      dispatch({
        type: 'UPDATE_MATTER_DRAFT_STATUS',
        payload: { entityType: 'matter', entityId: matterId, draftId, version, status: 'Final' },
      });
    } else {
      handleApiError(response, dispatch);
    }
  } catch (err) {
    handleApiError(err, dispatch);
  }
};

export const fetchMatterDraftVersions = (
  matterId: string,
  draftId: number
): AppThunk<Promise<any>> => async dispatch => {
  try {
    const response = await apis.get('/matter-draft/versions', {
      params: { matter_id: matterId, draft_id: draftId },
    });
    if (response.status === 200) return response.data;
    else if (response.status === 404) return [];
    handleApiError(response, dispatch);
    return null;
  } catch (err) {
    handleApiError(err, dispatch);
    return null;
  }
};

export const downloadMatterDraft = (
  matterId: string,
  draftId: number,
  version: number,
  htmlContent: string,
  format: 'docx' | 'pdf',
  branding: boolean,
  topLevelTypeName: string,
  brandingTemplateId?: string
): AppThunk<Promise<void>> => async dispatch => {
  try {
    const response = await apis.post(
      '/drafts/download',
      {
        matter_id: matterId,
        draft_id: draftId,
        version,
        html_content: htmlContent,
        format,
        branding,
        top_level_type_name: topLevelTypeName,
        branding_template_id: brandingTemplateId,
      },
      { responseType: 'blob' }
    );
    const mimeType = format === 'docx' ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' : 'application/pdf';
    const blob = new Blob([response.data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Matter_Draft_${draftId}.${format}`;
    a.click();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    handleApiError(err, dispatch);
  }
};

export const updateMatterDraftTypes = (matterId: string, draftTypeIds: number[]) => async (dispatch: any) => {
  try {
    const response = await apis.post(`/matters/${matterId}/drafttypes`, { drafttypes: draftTypeIds });
    dispatch(showFeedbackModal({
      showModal: true,
      message: 'Draft types updated successfully!',
      modalType: 'success',
      duration: 3000,
    }));
    return response.data;
  } catch (error) {
    console.error('Error updating matter draft types:', error);
    throw error;
  }
};

export const fetchMatterDraftTemplates = () => async (dispatch: any) => {
  try {
    const response = await apis.get('/matter-draft/templates');
    return response.data;
  } catch (error) {
    console.error('Error fetching matter draft templates:', error);
    throw error;
  }
};

export const createMatterDraftTemplate = (template: { name: string; description: string; draftTypeIds: number[] }) => async (dispatch: any) => {
  try {
    const response = await apis.post('/matter-draft/templates', template);
    dispatch(showFeedbackModal({ showModal: true, message: 'Template created successfully!', modalType: 'success', duration: 3000 }));
    return response.data;
  } catch (error) {
    console.error('Error creating matter draft template:', error);
    throw error;
  }
};

export const updateMatterDraftTemplate = (templateId: number, template: { name: string; description: string; draftTypeIds: number[] }) => async (dispatch: any) => {
  try {
    const response = await apis.put(`/matter-draft/templates/${templateId}`, template);
    dispatch(showFeedbackModal({
      showModal: true,
      message: 'Template updated successfully!',
      modalType: 'success',
      duration: 3000,
    }));
    return response.data;
  } catch (error) {
    console.error('Error updating matter draft template:', error);
    throw error;
  }
};

export const deleteMatterDraftTemplate = (templateId: number) => async (dispatch: any) => {
  try {
    const response = await apis.delete(`/matter-draft/templates/${templateId}`);
    dispatch(showFeedbackModal({
      showModal: true,
      message: 'Template deleted successfully!',
      modalType: 'success',
      duration: 3000,
    }));
    return response.data;
  } catch (error) {
    console.error('Error deleting matter draft template:', error);
    throw error;
  }
};

export const updateMatterDraftSettings = (values: any) => async (dispatch: any) => {
  try {
    const response = await apis.post('/matter-draft/settings/update', values);
    if (response && response.status === 200) return response.data;
  } catch (error) {
    console.error('Error updating matter draft settings:', error);
    handleApiError(error, dispatch);
    return null;
  }
};

export const getMatterDraftSettings = (matterId: string, draftId: number) => async (dispatch: any) => {
  try {
    const response = await apis.get(`/matter-draft/${matterId}/${draftId}/settings`);
    return response.data;
  } catch (error) {
    console.error('Error fetching matter draft settings:', error);
    throw error;
  }
};

export const setMatterCustomInstruction = (matterId: string, draftId: number, instruction: string) => async (dispatch: any) => {
  try {
    const response = await apis.post(`/matter-draft/${matterId}/${draftId}/custom_instruction`, { instruction });
    dispatch(showFeedbackModal({ showModal: true, message: 'Custom instruction updated successfully!', modalType: 'success', duration: 3000 }));
    return response.data;
  } catch (error) {
    console.error('Error setting matter custom instruction:', error);
    throw error;
  }
};

export const addMatterDraftAttachment = (matterId: string, draftId: number, file: File) => async (dispatch: any) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apis.post(`/matter-draft/${matterId}/${draftId}/attachments`, formData);
    dispatch(showFeedbackModal({ showModal: true, message: 'Attachment added successfully!', modalType: 'success', duration: 3000 }));
    return response.data;
  } catch (error) {
    console.error('Error adding matter attachment:', error);
    throw error;
  }
};

export const removeMatterDraftAttachment = (matterId: string, draftId: number, attachmentIndex: number) => async (dispatch: any) => {
  try {
    const response = await apis.delete(`/matter-draft/${matterId}/${draftId}/attachments/${attachmentIndex}`);
    dispatch(showFeedbackModal({ showModal: true, message: 'Attachment removed successfully!', modalType: 'success', duration: 3000 }));
    return response.data;
  } catch (error) {
    console.error('Error removing matter attachment:', error);
    throw error;
  }
};

export const fetchMatterDraftTypeFiles = (matterId: string, draftTypeName: string): AppThunk<Promise<any>> => async dispatch => {
  try {
    const response = await apis.get(`/matters/${matterId}/drafttypes/${draftTypeName}/files`);
    if (response.status === 200) return response.data;
    handleApiError(response, dispatch);
    return null;
  } catch (err) {
    handleApiError(err, dispatch);
    return null;
  }
};

export const uploadMatterDraftTypeFile = (matterId: string, draftTypeName: string, file: File): AppThunk<Promise<void>> => async dispatch => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apis.post(`/matters/${matterId}/drafttypes/${draftTypeName}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.status === 200) {
      dispatch(showFeedbackModal({ showModal: true, message: 'File uploaded successfully!', modalType: 'success', duration: 3000 }));
    } else {
      handleApiError(response, dispatch);
    }
  } catch (err) {
    handleApiError(err, dispatch);
  }
};
